import { generateRecurrenceOccurrences, type RecurrenceFrequency } from "@/lib/recurrence";

export type StayDayStatus = "scheduled" | "checked_in" | "checked_out" | "cancelled" | "no_show";

/** Dias que contam como estadia realizada — o que o histórico mostra. */
export const REALIZED_STAY_DAY_STATUSES: StayDayStatus[] = ["checked_in", "checked_out"];

export interface StayDayRecurrenceInput {
  frequency: "weekly" | "biweekly" | "monthly";
  interval: number;
  weekdays: number[];
  monthday: number | null;
  /** Obrigatório: recorrência de estadia sempre tem fim (máx. 12 meses). */
  endsOn: string;
}

export interface StayDaySourceInput {
  moduleType: "daycare" | "hotel" | "both";
  checkInDate: string;
  checkOutDate: string | null;
  recurrence: StayDayRecurrenceInput | null;
  /** Datas marcadas à mão. Quando há alguma, valem no lugar da regra e do dia único. */
  pickedDates?: string[];
  /** Datas tiradas da lista gerada (chip removido). */
  excludedDates?: string[];
}

/** `YYYY-MM-DD` local → Date no meio-dia local, imune a fuso. */
const parseLocalDate = (iso: string): Date => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const toLocalIso = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const nightsBetween = (checkInDate: string, checkOutDate: string): string[] => {
  const dates: string[] = [];
  const cursor = parseLocalDate(checkInDate);
  const end = parseLocalDate(checkOutDate);
  while (cursor < end) {
    dates.push(toLocalIso(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

/**
 * `stay_reservation_recurrence.frequency` tem "biweekly", que `@/lib/recurrence`
 * não conhece: lá isso é "weekly" com intervalo 2.
 */
const toRecurrenceRule = (recurrence: StayDayRecurrenceInput) => {
  const frequency: RecurrenceFrequency = recurrence.frequency === "monthly" ? "monthly" : "weekly";
  const interval = recurrence.frequency === "biweekly" ? 2 : Math.max(1, recurrence.interval);
  return { frequency, interval, weekdays: recurrence.weekdays, monthday: recurrence.monthday };
};

/**
 * As datas que a reserva ocupa. Hotel conta noites (a data de check-out não
 * gera pernoite); creche conta dias da regra de recorrência, ou o dia único.
 */
export function buildStayDayDates(input: StayDaySourceInput): string[] {
  const excluded = new Set(input.excludedDates ?? []);
  const keep = (dates: string[]) => [...new Set(dates)].filter((date) => !excluded.has(date)).sort();

  if (input.pickedDates && input.pickedDates.length > 0) {
    return keep(input.pickedDates);
  }

  if (input.recurrence) {
    const start = parseLocalDate(input.checkInDate);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const rule = toRecurrenceRule(input.recurrence);
    const endsOn = parseLocalDate(input.recurrence.endsOn);
    const windowDays = Math.ceil((endsOn.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    const occurrences = generateRecurrenceOccurrences(start, end, {
      ...rule,
      endsOn,
      windowDays: Math.max(1, windowDays),
    });
    return keep(occurrences.map((occurrence) => occurrence.occurrenceDate));
  }

  if (input.moduleType === "hotel" && input.checkOutDate && input.checkOutDate > input.checkInDate) {
    return keep(nightsBetween(input.checkInDate, input.checkOutDate));
  }

  return keep([input.checkInDate]);
}

export interface ExistingStayDay {
  id: string;
  stayDate: string;
  status: StayDayStatus;
}

export interface StayDayReconciliation {
  datesToInsert: string[];
  dayIdsToDelete: string[];
}

/** Dias que a edição da reserva não pode apagar: já aconteceram ou foram decididos à mão. */
const PROTECTED_STATUSES = new Set<StayDayStatus>(["checked_in", "checked_out", "cancelled", "no_show"]);

/**
 * Compara os dias já gravados com os que a regra atual pede. Dias `scheduled`
 * são regerados livremente; dias protegidos permanecem, mesmo que a nova regra
 * não os inclua.
 */
export function reconcileStayDays(args: {
  existing: ExistingStayDay[];
  desiredDates: string[];
}): StayDayReconciliation {
  const desired = new Set(args.desiredDates);
  const existingDates = new Set(args.existing.map((day) => day.stayDate));

  const datesToInsert = args.desiredDates.filter((date) => !existingDates.has(date));
  const dayIdsToDelete = args.existing
    .filter((day) => !PROTECTED_STATUSES.has(day.status) && !desired.has(day.stayDate))
    .map((day) => day.id);

  return { datesToInsert, dayIdsToDelete };
}
